import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import { useContext, useState, type ReactNode } from "react";
import toast from "react-hot-toast";

import {
  downloadAiMessagePdf,
  type AiPdfPart,
} from "@/services/(my-space)/aiAssistantPdfDownload";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";

import { PdfExportContext } from "./pdf-export-context";

/**
 * Wraps a table or chart in an AI reply with a "Save PDF" button for just that
 * element (server-rendered, so it matches the chat). The button appears on
 * hover or keyboard focus.
 */
export const PdfDownloadable = ({
  children,
  part,
  index,
  className,
}: {
  children: ReactNode;
  part: Exclude<AiPdfPart, "full">;
  index: number;
  className?: string;
}) => {
  const { t } = useTranslation("my-space");
  const ctx = useContext(PdfExportContext);
  const messageId = ctx?.messageId ?? null;
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (isDownloading) return;
    if (messageId == null) {
      toast.error(t("toasts.save_this_chat_before_exporting"));
      return;
    }
    setIsDownloading(true);
    try {
      await toast.promise(downloadAiMessagePdf({ messageId, part, index }), {
        loading: t("ui.generating_pdf"),
        success: t("ui.pdf_saved"),
        error: (e) => (e as Error)?.message ?? t("toasts.failed_to_save_pdf"),
      });
    } catch {
      // toast.promise surfaces the error
    } finally {
      setIsDownloading(false);
    }
  };

  if (messageId == null && ctx?.hideUnsaved) {
    return (
      <div className={cn("relative", className)}>
        <div>{children}</div>
      </div>
    );
  }

  return (
    <div className={cn("group/pdf relative", className)}>
      <div>{children}</div>
      <Button
        type="button"
        variant="outline"
        size="xs"
        onClick={handleDownload}
        disabled={isDownloading}
        title={t("labels.save_as_pdf")}
        className="absolute end-1 top-1 h-6 gap-1 rounded-full bg-surface/90 text-muted-foreground opacity-0 transition-opacity hover:bg-surface hover:text-foreground focus-visible:opacity-100 group-hover/pdf:opacity-100 disabled:opacity-60"
      >
        <Download className="size-3" />
        {isDownloading ? t("ui.saving_2_2") : t("ui.save_pdf")}
      </Button>
    </div>
  );
};
